import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Shield, Lock, Database, Trash2, Mail, ArrowLeft } from 'lucide-react';

const PrivacyPolicy = () => {
  const navigate = useNavigate();
  const [language, setLanguage] = useState('fr');

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour
          </Button>
          <div className="flex gap-2">
            <Button 
              variant={language === 'fr' ? 'default' : 'outline'} 
              size="sm"
              onClick={() => setLanguage('fr')}
            >
              FR
            </Button>
            <Button 
              variant={language === 'en' ? 'default' : 'outline'} 
              size="sm"
              onClick={() => setLanguage('en')}
            >
              EN
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Title */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/20 rounded-2xl mb-4">
              <Shield className="w-8 h-8 text-primary" />
            </div>
            <h1 className="text-3xl md:text-4xl font-heading font-bold mb-2">
              {language === 'fr' ? 'Politique de Confidentialité' : 'Privacy Policy'}
            </h1>
            <p className="text-muted">
              {language === 'fr' 
                ? 'Dernière mise à jour : Février 2026' 
                : 'Last updated: February 2026'}
            </p>
          </div>

          {language === 'fr' ? (
            <div className="prose prose-invert max-w-none">
              {/* Introduction */}
              <section className="mb-10">
                <h2 className="text-xl font-heading font-semibold mb-4 flex items-center gap-2">
                  <span className="text-primary">1.</span> Introduction
                </h2>
                <p className="text-muted leading-relaxed">
                  Boost-StrategIA, édité par Mariposa Consulting LLC, s'engage à protéger la vie privée 
                  de ses utilisateurs. Cette politique de confidentialité explique comment nous collectons, 
                  utilisons, stockons et protégeons vos données personnelles lorsque vous utilisez notre 
                  service de génération de stratégie marketing.
                </p>
              </section>

              {/* Data Collected */}
              <section className="mb-10">
                <h2 className="text-xl font-heading font-semibold mb-4 flex items-center gap-2">
                  <Database className="w-5 h-5 text-primary" />
                  <span className="text-primary">2.</span> Données collectées
                </h2>
                <div className="bg-card border border-border rounded-lg p-6">
                  <h3 className="font-semibold mb-3">Données d'identification :</h3>
                  <ul className="list-disc list-inside text-muted space-y-1 mb-4">
                    <li>Adresse email</li>
                    <li>Nom et prénom (optionnel)</li>
                    <li>Mot de passe (stocké de manière chiffrée)</li>
                  </ul>
                  
                  <h3 className="font-semibold mb-3">Données d'utilisation :</h3>
                  <ul className="list-disc list-inside text-muted space-y-1 mb-4">
                    <li>Réponses aux questionnaires d'onboarding</li>
                    <li>Contenu des études marketing générées</li>
                    <li>Historique des conversations avec l'assistant IA</li>
                    <li>Réponses au module "Légende Personnelle"</li>
                  </ul>
                  
                  <h3 className="font-semibold mb-3">Données de paiement :</h3>
                  <ul className="list-disc list-inside text-muted space-y-1">
                    <li>Informations de facturation (gérées par Stripe)</li>
                    <li>Historique des transactions</li>
                  </ul>
                </div>
              </section>

              {/* Storage */}
              <section className="mb-10">
                <h2 className="text-xl font-heading font-semibold mb-4 flex items-center gap-2">
                  <Lock className="w-5 h-5 text-primary" />
                  <span className="text-primary">3.</span> Stockage et sécurité
                </h2>
                <div className="bg-card border border-border rounded-lg p-6">
                  <p className="text-muted mb-4">
                    Vos données sont stockées sur des serveurs sécurisés avec les mesures de protection suivantes :
                  </p>
                  <ul className="list-disc list-inside text-muted space-y-2">
                    <li><strong>Chiffrement des mots de passe</strong> : Algorithme bcrypt (impossible à déchiffrer)</li>
                    <li><strong>Connexions sécurisées</strong> : Protocole HTTPS pour toutes les communications</li>
                    <li><strong>Authentification</strong> : Tokens JWT avec expiration automatique</li>
                    <li><strong>Paiements</strong> : Traités par Stripe (certifié PCI-DSS Level 1)</li>
                    <li><strong>Accès restreint</strong> : Seul l'administrateur peut accéder aux données utilisateurs</li>
                  </ul>
                </div>
              </section>

              {/* Usage */}
              <section className="mb-10">
                <h2 className="text-xl font-heading font-semibold mb-4 flex items-center gap-2">
                  <span className="text-primary">4.</span> Utilisation des données
                </h2>
                <p className="text-muted mb-4">Nous utilisons vos données exclusivement pour :</p>
                <ul className="list-disc list-inside text-muted space-y-2">
                  <li>Fournir et personnaliser nos services de stratégie marketing</li>
                  <li>Gérer votre compte et vos abonnements</li>
                  <li>Vous envoyer des communications relatives à votre compte</li>
                  <li>Améliorer nos services (analyses anonymisées)</li>
                </ul>
                <div className="mt-4 p-4 bg-highlight/10 border border-highlight/30 rounded-lg">
                  <p className="text-sm">
                    <strong>Important :</strong> Nous ne vendons jamais vos données à des tiers. 
                    Vos études et informations business restent strictement confidentielles.
                  </p>
                </div>
              </section>

              {/* Rights */}
              <section className="mb-10">
                <h2 className="text-xl font-heading font-semibold mb-4 flex items-center gap-2">
                  <span className="text-primary">5.</span> Vos droits (RGPD)
                </h2>
                <p className="text-muted mb-4">
                  Conformément au Règlement Général sur la Protection des Données (RGPD), vous disposez des droits suivants :
                </p>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-card border border-border rounded-lg p-4">
                    <h4 className="font-semibold mb-2">Droit d'accès</h4>
                    <p className="text-sm text-muted">Obtenir une copie de toutes vos données personnelles</p>
                  </div>
                  <div className="bg-card border border-border rounded-lg p-4">
                    <h4 className="font-semibold mb-2">Droit de rectification</h4>
                    <p className="text-sm text-muted">Corriger des informations inexactes ou incomplètes</p>
                  </div>
                  <div className="bg-card border border-border rounded-lg p-4">
                    <h4 className="font-semibold mb-2">Droit à l'effacement</h4>
                    <p className="text-sm text-muted">Demander la suppression de vos données</p>
                  </div>
                  <div className="bg-card border border-border rounded-lg p-4">
                    <h4 className="font-semibold mb-2">Droit à la portabilité</h4>
                    <p className="text-sm text-muted">Recevoir vos données dans un format standard</p>
                  </div>
                </div>
              </section>

              {/* Deletion */}
              <section className="mb-10">
                <h2 className="text-xl font-heading font-semibold mb-4 flex items-center gap-2">
                  <Trash2 className="w-5 h-5 text-primary" />
                  <span className="text-primary">6.</span> Suppression des données
                </h2>
                <p className="text-muted mb-4">
                  Vous pouvez demander la suppression complète de votre compte et de toutes vos données à tout moment.
                  Pour cela, contactez-nous à l'adresse ci-dessous. La suppression sera effectuée sous 30 jours.
                </p>
                <p className="text-muted">
                  Note : Certaines données peuvent être conservées pour des obligations légales (facturation).
                </p>
              </section>

              {/* Contact */}
              <section className="mb-10">
                <h2 className="text-xl font-heading font-semibold mb-4 flex items-center gap-2">
                  <Mail className="w-5 h-5 text-primary" />
                  <span className="text-primary">7.</span> Contact
                </h2>
                <div className="bg-card border border-border rounded-lg p-6">
                  <p className="text-muted mb-4">
                    Pour toute question concernant cette politique de confidentialité ou pour exercer vos droits, 
                    contactez notre Délégué à la Protection des Données :
                  </p>
                  <p className="font-medium">Mariposa Consulting LLC</p>
                  <p className="text-muted">Email : contact@boost-strategia.com</p>
                </div>
              </section>

              {/* Cookies */}
              <section className="mb-10">
                <h2 className="text-xl font-heading font-semibold mb-4 flex items-center gap-2">
                  <span className="text-primary">8.</span> Cookies
                </h2>
                <p className="text-muted">
                  Notre application utilise uniquement des cookies techniques essentiels au fonctionnement 
                  du service (authentification, préférences). Nous n'utilisons pas de cookies publicitaires 
                  ou de traceurs tiers.
                </p>
              </section>

              {/* Updates */}
              <section>
                <h2 className="text-xl font-heading font-semibold mb-4 flex items-center gap-2">
                  <span className="text-primary">9.</span> Modifications
                </h2>
                <p className="text-muted">
                  Cette politique peut être mise à jour occasionnellement. Nous vous informerons de tout 
                  changement significatif par email. La date de dernière mise à jour est indiquée en haut 
                  de cette page.
                </p>
              </section>
            </div>
          ) : (
            <div className="prose prose-invert max-w-none">
              {/* English Version */}
              <section className="mb-10">
                <h2 className="text-xl font-heading font-semibold mb-4 flex items-center gap-2">
                  <span className="text-primary">1.</span> Introduction
                </h2>
                <p className="text-muted leading-relaxed">
                  Boost-StrategIA, operated by Mariposa Consulting LLC, is committed to protecting the privacy 
                  of its users. This privacy policy explains how we collect, use, store and protect your personal 
                  data when you use our marketing strategy generation service.
                </p>
              </section>

              <section className="mb-10">
                <h2 className="text-xl font-heading font-semibold mb-4 flex items-center gap-2">
                  <Database className="w-5 h-5 text-primary" />
                  <span className="text-primary">2.</span> Data Collected
                </h2>
                <div className="bg-card border border-border rounded-lg p-6">
                  <h3 className="font-semibold mb-3">Identification Data:</h3>
                  <ul className="list-disc list-inside text-muted space-y-1 mb-4">
                    <li>Email address</li>
                    <li>First and last name (optional)</li>
                    <li>Password (stored encrypted)</li>
                  </ul>
                  
                  <h3 className="font-semibold mb-3">Usage Data:</h3>
                  <ul className="list-disc list-inside text-muted space-y-1 mb-4">
                    <li>Onboarding questionnaire responses</li>
                    <li>Generated marketing studies content</li>
                    <li>AI assistant conversation history</li>
                    <li>"Personal Legend" module responses</li>
                  </ul>
                  
                  <h3 className="font-semibold mb-3">Payment Data:</h3>
                  <ul className="list-disc list-inside text-muted space-y-1">
                    <li>Billing information (managed by Stripe)</li>
                    <li>Transaction history</li>
                  </ul>
                </div>
              </section>

              <section className="mb-10">
                <h2 className="text-xl font-heading font-semibold mb-4 flex items-center gap-2">
                  <Lock className="w-5 h-5 text-primary" />
                  <span className="text-primary">3.</span> Storage and Security
                </h2>
                <div className="bg-card border border-border rounded-lg p-6">
                  <p className="text-muted mb-4">
                    Your data is stored on secure servers with the following protection measures:
                  </p>
                  <ul className="list-disc list-inside text-muted space-y-2">
                    <li><strong>Password encryption</strong>: bcrypt algorithm (impossible to decrypt)</li>
                    <li><strong>Secure connections</strong>: HTTPS protocol for all communications</li>
                    <li><strong>Authentication</strong>: JWT tokens with automatic expiration</li>
                    <li><strong>Payments</strong>: Processed by Stripe (PCI-DSS Level 1 certified)</li>
                    <li><strong>Restricted access</strong>: Only the administrator can access user data</li>
                  </ul>
                </div>
              </section>

              <section className="mb-10">
                <h2 className="text-xl font-heading font-semibold mb-4 flex items-center gap-2">
                  <span className="text-primary">4.</span> Data Usage
                </h2>
                <p className="text-muted mb-4">We use your data exclusively to:</p>
                <ul className="list-disc list-inside text-muted space-y-2">
                  <li>Provide and personalize our marketing strategy services</li>
                  <li>Manage your account and subscriptions</li>
                  <li>Send you account-related communications</li>
                  <li>Improve our services (anonymized analytics)</li>
                </ul>
                <div className="mt-4 p-4 bg-highlight/10 border border-highlight/30 rounded-lg">
                  <p className="text-sm">
                    <strong>Important:</strong> We never sell your data to third parties. 
                    Your studies and business information remain strictly confidential.
                  </p>
                </div>
              </section>

              <section className="mb-10">
                <h2 className="text-xl font-heading font-semibold mb-4 flex items-center gap-2">
                  <span className="text-primary">5.</span> Your Rights (GDPR)
                </h2>
                <p className="text-muted mb-4">
                  Under the General Data Protection Regulation (GDPR), you have the following rights:
                </p>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-card border border-border rounded-lg p-4">
                    <h4 className="font-semibold mb-2">Right of Access</h4>
                    <p className="text-sm text-muted">Obtain a copy of all your personal data</p>
                  </div>
                  <div className="bg-card border border-border rounded-lg p-4">
                    <h4 className="font-semibold mb-2">Right to Rectification</h4>
                    <p className="text-sm text-muted">Correct inaccurate or incomplete information</p>
                  </div>
                  <div className="bg-card border border-border rounded-lg p-4">
                    <h4 className="font-semibold mb-2">Right to Erasure</h4>
                    <p className="text-sm text-muted">Request deletion of your data</p>
                  </div>
                  <div className="bg-card border border-border rounded-lg p-4">
                    <h4 className="font-semibold mb-2">Right to Portability</h4>
                    <p className="text-sm text-muted">Receive your data in a standard format</p>
                  </div>
                </div>
              </section>

              <section className="mb-10">
                <h2 className="text-xl font-heading font-semibold mb-4 flex items-center gap-2">
                  <Mail className="w-5 h-5 text-primary" />
                  <span className="text-primary">6.</span> Contact
                </h2>
                <div className="bg-card border border-border rounded-lg p-6">
                  <p className="text-muted mb-4">
                    For any questions regarding this privacy policy or to exercise your rights, 
                    contact our Data Protection Officer:
                  </p>
                  <p className="font-medium">Mariposa Consulting LLC</p>
                  <p className="text-muted">Email: contact@boost-strategia.com</p>
                </div>
              </section>
            </div>
          )}

          {/* Back button */}
          <div className="mt-12 text-center">
            <Button onClick={() => navigate('/')}>
              Retour à l'accueil
            </Button>
          </div>
        </motion.div>
      </main>
    </div>
  );
};

export default PrivacyPolicy;
